import { useEffect, useState } from "react";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { Tooltip, Typography } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import EditIcon from "@mui/icons-material/Edit";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import EuroIcon from "@mui/icons-material/Euro";
import { billingService } from "../../../Core/Api/services";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import { DbInvoiceInfos } from "../../../Core/Api/Types/db-invoice-infos";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../Components/Misc/ListPageHeader";
import DataTable, { CellSize } from "../../../Components/Table/table";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import TableCellBillingStatus from "../../../Components/Table/TableCellBillingStatus/table-cell-billing-status";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { UserHasAcl } from "../../../Utils/helpers";
import React from "react";
import { ModalContext } from "../../../Components/Modals";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { BillingStatus } from "../../../Core/Api/Types/billing-status";
import ModalBillingAddExpense, {
    ModalBillingAddExpenseProps,
} from "../../../Components/Modals/ModalBillingAddExpense/modal-billing-add-expense";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../Components/Table/table-uuids";

const BillingInvoices = (props) => {
    const { t } = useTranslation();
    const enqueueCustomSnackbar = useCustomSnackbar();
    const navigate = useNavigate();
    const { pageSize = 20 } = props;
    const [invoices, setInvoices] = useState<DbInvoiceInfos[]>([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(0);

    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const aclSuperUser = UserHasAcl(AclTypes.aclSuperUser);
    const aclBillingValidateInvoice = UserHasAcl(AclTypes.aclBillingValidateInvoice);

    useEffect(() => {
        billingService.ListInvoices().then((response) => {
            if (response.invoices) setInvoices(response.invoices);

            setLoading(false);
        });
    }, [reload]);

    const handleValidateInvoice = (invoiceId: number, status: BillingStatus) => {
        if (!openModal) return;
        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            displayComment: false,
            title: t("modal:title.confirmPassInvoice", { id: invoiceId, status }),
            onConfirm: () => {
                billingService.ValidateInvoice(invoiceId).then((response) => {
                    if (response.success) {
                        enqueueCustomSnackbar(t("alert:success.invoiceHasBeenChangedStatus", { id: invoiceId, status }), "success");
                        setReload((prev) => prev + 1);
                    } else
                        enqueueCustomSnackbar(
                            t("alert:error.changingStatusOfInvoice", { id: invoiceId, message: response.message }),
                            "error"
                        );
                });
            },
        });
    };

    const handleAddExpense = (invoiceId: number) => {
        if (!openModal) return;

        billingService.GetMySubscription().then((response) => {
            if (response.success) {
                openModal<ModalBillingAddExpenseProps>(ModalBillingAddExpense, {
                    invoiceId: invoiceId,
                    priceModel: response.priceModel,
                    onSuccess: () => {
                        setReload((prev) => prev + 1);
                    },
                    closeModal: () => { },
                    onClose: () => { },
                });
            }
        });
    };

    // Cols def
    const cols: GridColDef[] = [
        {
            field: "reference",
            headerName: t("common:reference"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            valueGetter: (data: any, row: any) => row?.id,
            renderCell: (data: any) => <Typography variant="body2">#{data.value}</Typography>,
        },
        {
            field: "date",
            headerName: t("common:date"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {moment.utc(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                </Typography>
            ),
        },
        {
            field: "startDate",
            headerName: t("common:startDateLowerCase"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {moment.utc(data.value, true).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                </Typography>
            ),
        },
        {
            field: "endDate",
            headerName: t("common:endDateLowerCase"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {moment.utc(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                </Typography>
            ),
        },

        {
            field: "status",
            headerName: t("common:status"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <TableCellBillingStatus data={data} />,
        },
        {
            field: "price",
            headerName: t("common:price"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {data.value.toFixed(2)} {data?.row?.currency}
                </Typography>
            ),
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                let actions = [
                    <Tooltip title={t("common:download")} placement="top" arrow>
                        <GridActionsCellItem
                            icon={<CloudDownloadIcon sx={{ width: 20, height: 20 }} />}
                            label={t("common:download")}
                            onClick={() => {
                                enqueueCustomSnackbar(t("alert:success.processingInvoiceExport"), "success");
                                billingService.DownloadBillingInvoicePdfExport(params?.row?.id).then((response) => {
                                    if (response) enqueueCustomSnackbar(t("alert:success.invoiceIsReady"), "success");
                                });
                            }}
                        />
                    </Tooltip>,
                    <Tooltip title={t("common:details")} placement="top" arrow>
                        <GridActionsCellItem
                            icon={<QueryStatsIcon sx={{ width: 20, height: 20 }} />}
                            label={t("common:details")}
                            onClick={() => navigate(`${routePaths.billingInvoices.path}/${params?.row?.id}`)}
                        />
                    </Tooltip>,
                ];

                if (aclSuperUser && params?.row?.status === "TO_BILL") {
                    actions.push(
                        <Tooltip title={t("common:editExpenses")} placement="top" arrow>
                            <GridActionsCellItem
                                icon={<EditIcon sx={{ width: 20, height: 20 }} color="info" />}
                                label={t("common:editExpenses")}
                                onClick={() => handleAddExpense(params?.row?.id)}
                            />
                        </Tooltip>
                    );

                    if (aclBillingValidateInvoice)
                        actions.push(
                            <Tooltip title={t("common:billInvoice")} placement="top" arrow>
                                <GridActionsCellItem
                                    icon={<CreditScoreIcon sx={{ width: 20, height: 20 }} color="success" />}
                                    label={t("common:billInvoice")}
                                    onClick={() => handleValidateInvoice(params?.row?.id, BillingStatus.BILLED)}
                                />
                            </Tooltip>
                        );
                }

                if (aclSuperUser && params?.row?.status === "BILLED" && aclBillingValidateInvoice) {
                    actions.push(
                        <Tooltip title={t("common:passToPaid")} placement="top" arrow>
                            <GridActionsCellItem
                                icon={<EuroIcon sx={{ width: 20, height: 20 }} color="success" />}
                                label={t("common:passToPaid")}
                                onClick={() => handleValidateInvoice(params?.row?.id, BillingStatus.PAID)}
                            />
                        </Tooltip>
                    );
                }

                return actions;
            },
        },
    ];

    return (
        <>
            {!loading && (
                <DataTable
                    tableKey={TableUuids.BILLING_INVOICES}
                    rows={invoices}
                    columns={cols}
                    columnHeaderHeight={48}
                    rowHeight={56}
                    getRowHeight={() => "auto"}
                    checkboxSelection={false}
                    pageSize={pageSize}
                    disableRowSelectionOnClick
                    onRowClick={(row) => navigate(`${routePaths.billingInvoices.path}/${row.row.id}`)}
                    pagination={true}
                />
            )}
            {loading && <TableSkeleton />}
        </>
    );
};

export default BillingInvoices;
